import { openai } from '@ai-sdk/openai';
import type { LlmCostAccounting } from '@edgevanta/bid-tab-extract';
import { type LanguageModel, generateText } from 'ai';

import { resolveCollectionFactor } from './collection-validation';
import { categoryDefinitions } from './definitions';
import { runAccountedCustomBidTabsModelCall } from '../model-cost-accounting';

// The on-the-fly item classifier: runs the SAME authoritative prompt the dbt
// batch classifier uses (`category-definitions.md`) over a single upload's
// items, optionally hinted with the categories of trigram-similar DOT items.
// Output parity with dbt: a category code, a lump-sum semantic, and validated
// collection assignments — the inputs to every downstream analytic surface.

/** One line to classify, with optional propagation hints (similar DOT items). */
export interface ClassifierInput {
  description: string;
  unit: string | null;
  hints?: Array<{ code: string | null; name: string; similarity: number }>;
}

/** Liquid AC pattern classified from the item description (dbt parity). */
export type LiquidAcPatternCode =
  | 'P1_allin_TN'
  | 'P2_allin_SY'
  | 'P3_allin_CY'
  | 'P4_liquid';

/** The enrichment decided for one line, ready for the domains write path. */
export interface ClassifierResult {
  categoryCode: string;
  /** true_pct_of_bid → true; fixed_cost_ls / not_lump_sum → false. */
  isTrueLumpSum: boolean;
  collections: Array<{ code: string; factor: number }>;
  /** The liquid AC adjustment pattern, or null when the item needs none. */
  liquidAcPattern: LiquidAcPatternCode | null;
}

// The output contract + lump-sum decision tree the dbt script appends to the
// definitions (they live in the runner, not the .md). Kept verbatim in intent.
const OUTPUT_INSTRUCTIONS = `
---

# Output Format

Respond with ONE compact JSON tuple per item, no markdown, no prose:
[<item index>,"XX",<is true lump sum>,[["<collection_code>",<factor>,"exact|parsed|default"]],"P1_allin_TN|P2_allin_SY|P3_allin_CY|P4_liquid|null"]

- item index: the index shown before the item in the user message.
- category code: the two-digit code (01–22, or 99 if truly uncategorizable).
- is true lump sum: a JSON boolean. Apply the lump-sum decision tree below; false includes fixed-cost lump sums and every non-LS item.
- collections: zero or more [collection code, conversion factor, source] tuples. Use [] when none apply.
- liquid AC pattern: whether the item is subject to liquid asphalt cement price adjustment, from its DESCRIPTION. Only asphalt/HMA/bituminous paving and asphalt binder items qualify.
  - P1_allin_TN: an all-in asphalt mix pay item paid by the TON (unit TON/TN).
  - P2_allin_SY: an all-in asphalt mix pay item paid by the SQUARE YARD (unit SY).
  - P3_allin_CY: an all-in asphalt mix pay item paid by the CUBIC YARD (unit CY).
  - P4_liquid: a STANDALONE liquid asphalt / asphalt binder / PG-graded binder pay item (the binder itself, priced separately).
  - null: anything that is not an asphalt-mix or asphalt-binder pay item (the default — most items).
- lump-sum decision tree (only meaningful when the unit is LUMP SUM, "LS"):
  1. Names ONE specific asset in the SINGULAR (one bridge/culvert/building/structure)? → fixed_cost_ls
  2. A compliance/regulatory plan or report? → fixed_cost_ls
  3. A contingency/allowance/unspecified MISC line? → fixed_cost_ls
  4. Otherwise (MOBILIZATION, TRAFFIC CONTROL, CLEARING & GRUBBING, …) → true_pct_of_bid
  Non-LS unit (TON, LF, CY, EA, SY, SF, …) → not_lump_sum.

When candidate categories from similar already-classified items are shown, treat
them as strong evidence — prefer a candidate's code unless the description
clearly belongs elsewhere.`;

/** The full system prompt: authoritative definitions + the output contract. */
export function classificationSystemPrompt(): string {
  return categoryDefinitions() + OUTPUT_INSTRUCTIONS;
}

/** Build the user message: numbered items, each with its propagation hints. */
export function buildClassificationUserPrompt(
  items: ClassifierInput[],
): string {
  const blocks = items.map((item, i) => {
    const hints =
      item.hints && item.hints.length > 0
        ? `\n    candidates: ${item.hints
            .map(
              (h) =>
                `${h.code ?? '??'} ${h.name} (sim ${h.similarity.toFixed(2)})`,
            )
            .join('; ')}`
        : '';
    return `[${i}] "${item.description}" (unit ${item.unit ?? '—'})${hints}`;
  });
  return `Classify these ${items.length} bid-tab items:\n${blocks.join('\n')}`;
}

interface RawVerdictObject {
  i?: number;
  category_code?: string;
  lump_sum_type?: string;
  collections?: Array<{ code?: string; factor?: number; source?: string }>;
  liquid_ac_pattern?: string | null;
}

interface RawVerdict {
  i?: number;
  categoryCode?: string;
  isTrueLumpSum?: boolean;
  collections?: Array<{ code?: string; factor?: number; source?: string }>;
  liquidAcPattern?: string | null;
}

const LIQUID_AC_PATTERNS = new Set<LiquidAcPatternCode>([
  'P1_allin_TN',
  'P2_allin_SY',
  'P3_allin_CY',
  'P4_liquid',
]);

function parseLiquidAcPattern(
  raw: string | null | undefined,
): LiquidAcPatternCode | null {
  return raw && LIQUID_AC_PATTERNS.has(raw as LiquidAcPatternCode)
    ? (raw as LiquidAcPatternCode)
    : null;
}

/**
 * Parse the model's per-line JSON into {@link ClassifierResult}, validating each
 * collection with the shared {@link resolveCollectionFactor} (drops assignments
 * it can't turn into a usable factor). Missing/garbled lines default to '99'.
 * Pure — unit-testable without a live model.
 */
export function parseClassificationOutput(
  text: string,
  items: ClassifierInput[],
): ClassifierResult[] {
  const byIndex = new Map<number, RawVerdict>();
  const normalizedText = text
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim();
  const parsedWhole = parseVerdicts(normalizedText);
  for (const verdict of parsedWhole) {
    if (typeof verdict.i === 'number') {
      byIndex.set(verdict.i, verdict);
    }
  }
  for (const line of normalizedText.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      continue;
    }
    const verdicts = parseVerdicts(trimmed);
    for (const verdict of verdicts) {
      if (typeof verdict.i === 'number') {
        byIndex.set(verdict.i, verdict);
      }
    }
  }

  return items.map((item, i): ClassifierResult => {
    const v = byIndex.get(i);
    const collections: ClassifierResult['collections'] = [];
    for (const c of v?.collections ?? []) {
      if (!c.code) {
        continue;
      }
      const resolved = resolveCollectionFactor(
        c.code,
        item.unit,
        typeof c.factor === 'number' ? c.factor : 1,
        c.source ?? 'default',
      );
      if (resolved.ok) {
        collections.push({ code: c.code, factor: resolved.factor });
      }
    }
    return {
      categoryCode: v?.categoryCode ?? '99',
      isTrueLumpSum: v?.isTrueLumpSum ?? false,
      collections,
      liquidAcPattern: parseLiquidAcPattern(v?.liquidAcPattern),
    };
  });
}

function parseVerdicts(text: string): RawVerdict[] {
  try {
    return normalizeVerdicts(JSON.parse(text) as unknown);
  } catch {
    return [];
  }
}

function normalizeVerdicts(parsed: unknown): RawVerdict[] {
  if (isTupleVerdict(parsed)) {
    return [normalizeTupleVerdict(parsed)];
  }
  if (Array.isArray(parsed)) {
    return parsed.flatMap(normalizeVerdicts);
  }
  if (!parsed || typeof parsed !== 'object') {
    return [];
  }
  const object = parsed as RawVerdictObject;
  return [
    {
      i: object.i,
      categoryCode: object.category_code,
      isTrueLumpSum: object.lump_sum_type === 'true_pct_of_bid',
      collections: object.collections,
      liquidAcPattern: object.liquid_ac_pattern,
    },
  ];
}

export class ClassificationOutputTruncatedError extends Error {
  constructor() {
    super('Item classification response was truncated.');
    this.name = 'ClassificationOutputTruncatedError';
  }
}

type TupleVerdict = [
  number,
  string?,
  boolean?,
  Array<[string?, number?, string?]>?,
  (string | null)?,
];

function isTupleVerdict(value: unknown): value is TupleVerdict {
  return Array.isArray(value) && typeof value[0] === 'number';
}

function normalizeTupleVerdict(tuple: TupleVerdict): RawVerdict {
  return {
    i: tuple[0],
    categoryCode: typeof tuple[1] === 'string' ? tuple[1] : undefined,
    isTrueLumpSum: typeof tuple[2] === 'boolean' ? tuple[2] : false,
    collections: Array.isArray(tuple[3])
      ? tuple[3].flatMap((collection) =>
          Array.isArray(collection)
            ? [
                {
                  code:
                    typeof collection[0] === 'string'
                      ? collection[0]
                      : undefined,
                  factor:
                    typeof collection[1] === 'number'
                      ? collection[1]
                      : undefined,
                  source:
                    typeof collection[2] === 'string'
                      ? collection[2]
                      : undefined,
                },
              ]
            : [],
        )
      : [],
    liquidAcPattern:
      typeof tuple[4] === 'string' || tuple[4] === null ? tuple[4] : undefined,
  };
}

/**
 * Classify a batch of items with the model (default the wired cheap OpenAI
 * model; pluggable so the eval harness can A/B). The system prompt is the
 * authoritative definitions — `categoryDefinitions()` throws if it failed to
 * load, so we never classify against an empty prompt.
 */
export async function classifyBidTabItems(
  items: ClassifierInput[],
  options: {
    model?: LanguageModel;
    costAccounting?: LlmCostAccounting;
    stage?: string;
  } = {},
): Promise<ClassifierResult[]> {
  if (items.length === 0) {
    return [];
  }
  const model = options.model ?? openai('gpt-4.1-mini');
  const { text, finishReason } = await runAccountedCustomBidTabsModelCall({
    stage: options.stage ?? 'normalize:classification',
    model,
    costAccounting: options.costAccounting,
    call: async () => {
      const result = await generateText({
        model,
        maxRetries: 0,
        maxOutputTokens: 32_000,
        system: classificationSystemPrompt(),
        prompt: buildClassificationUserPrompt(items),
      });
      return {
        value: { text: result.text, finishReason: result.finishReason },
        usage: result.usage,
        providerMetadata: result.providerMetadata,
      };
    },
  });
  if (finishReason === 'length') {
    throw new ClassificationOutputTruncatedError();
  }
  if (finishReason !== 'stop') {
    throw new Error(
      `Item classification stopped before completion (${finishReason}).`,
    );
  }
  return parseClassificationOutput(text, items);
}
